import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Logo } from '../../components/Logo';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import { ChevronLeft } from 'lucide-react';
import { LAST_UPDATED, UI } from './legal-content';

const SUPPORTED = ['it', 'en', 'fr', 'de', 'es'];

/**
 * Renders inline markdown-like syntax: **bold** and *italic*.
 */
function renderInline(text) {
    if (!text) return null;
    // First pass: bold
    const parts = [];
    let lastIdx = 0;
    const boldRe = /\*\*(.+?)\*\*/g;
    let m;
    while ((m = boldRe.exec(text)) !== null) {
        if (m.index > lastIdx) parts.push(text.slice(lastIdx, m.index));
        parts.push(<strong key={`b-${m.index}`}>{m[1]}</strong>);
        lastIdx = m.index + m[0].length;
    }
    if (lastIdx < text.length) parts.push(text.slice(lastIdx));

    // Second pass on plain text fragments: italic
    return parts.flatMap((part, i) => {
        if (typeof part !== 'string') return [part];
        const subParts = [];
        let subLast = 0;
        const italicRe = /(?<!\*)\*([^*]+?)\*(?!\*)/g;
        let im;
        while ((im = italicRe.exec(part)) !== null) {
            if (im.index > subLast) subParts.push(part.slice(subLast, im.index));
            subParts.push(<em key={`i-${i}-${im.index}`}>{im[1]}</em>);
            subLast = im.index + im[0].length;
        }
        if (subLast < part.length) subParts.push(part.slice(subLast));
        return subParts.length ? subParts : [part];
    });
}

/**
 * LegalPage — shared layout for Privacy / Cookies / Terms.
 *
 * Props:
 * - icon: lucide icon component
 * - content: { it: {title, sections, footer?}, en: {...}, ... }
 *   sections: [{ h, p?: string[], ul?: string[], pAfter?: string[], table?: { headers, rows } }]
 * - extraFooter: optional ReactNode rendered below content (for cross-links etc.)
 */
export function LegalPage({ icon: Icon, content, extraFooter }) {
    const { i18n } = useTranslation();
    const lang = SUPPORTED.includes((i18n.language || 'it').split('-')[0]) ? (i18n.language || 'it').split('-')[0] : 'it';
    const ui = UI[lang];
    const c = content[lang] || content.it;

    return (
        <div className="min-h-screen bg-white">
            <header className="bg-[hsl(220_13%_10%)] text-white py-5">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-3">
                    <Link to="/" data-testid="legal-home-logo"><Logo variant="light" /></Link>
                    <div className="flex items-center gap-3">
                        <LanguageSwitcher variant="dark" />
                        <Link to="/" className="text-sm flex items-center gap-1 text-white/70 hover:text-[hsl(var(--accent))]" data-testid="legal-home-link">
                            <ChevronLeft className="w-4 h-4" /> {ui.home}
                        </Link>
                    </div>
                </div>
            </header>
            <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
                <div className="flex items-center gap-3 mb-2 text-xs tracking-widest text-[hsl(var(--muted-foreground))]">
                    {Icon && <Icon className="w-4 h-4" />} {ui.legal_doc}
                </div>
                <h1 className="font-display text-5xl mb-2" data-testid="legal-title">{c.title}</h1>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mb-8">
                    {ui.last_updated}: {LAST_UPDATED[lang]} · {ui.version} 1.0.0
                </p>

                {lang !== 'it' && (
                    <div className="mb-6 p-3 rounded-lg border border-[hsl(var(--accent))]/30 bg-[hsl(45_100%_52%)]/5 text-xs text-[hsl(220_13%_25%)]" data-testid="legal-it-prevails-notice">
                        <strong>⚖️</strong> {ui.it_prevails}
                    </div>
                )}

                <div className="prose prose-sm max-w-none space-y-5 text-[hsl(220_13%_20%)]">
                    {c.sections.map((s, i) => (
                        <section key={i}>
                            <h2 className="font-display text-2xl mt-6 mb-2">{s.h}</h2>
                            {(s.p || []).map((para, j) => (
                                <p key={`p-${j}`} className="mb-2">{renderInline(para)}</p>
                            ))}
                            {s.ul && (
                                <ul className="list-disc list-inside space-y-1">
                                    {s.ul.map((li, k) => <li key={k}>{renderInline(li)}</li>)}
                                </ul>
                            )}
                            {(s.pAfter || []).map((para, j) => (
                                <p key={`pa-${j}`} className="mt-2">{renderInline(para)}</p>
                            ))}
                            {s.table && (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-xs border-collapse mt-3">
                                        <thead className="bg-[hsl(var(--muted))]">
                                            <tr>{s.table.headers.map((h, ix) => <th key={ix} className="p-2 text-left border">{h}</th>)}</tr>
                                        </thead>
                                        <tbody>
                                            {s.table.rows.map((row, rx) => (
                                                <tr key={rx}>
                                                    {row.map((cell, cx) => <td key={cx} className="p-2 border">{renderInline(cell)}</td>)}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                    ))}

                    {c.footer && (
                        <div className="mt-10 p-4 rounded-lg bg-[hsl(var(--muted))]/40 text-xs text-[hsl(var(--muted-foreground))]">
                            {c.footer}
                        </div>
                    )}

                    {extraFooter && (
                        <div className="mt-6 p-4 rounded-lg bg-[hsl(var(--muted))]/40 text-xs text-[hsl(var(--muted-foreground))]">
                            <span className="font-semibold">{ui.see_also}:</span> {extraFooter}
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}

export default LegalPage;
